import { useState } from "react";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmailEventDetailsPanel } from "./EmailEventDetailsPanel";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { RefreshCw, ChevronRight } from "lucide-react";
import { AnalyticsEmailEvent, PaginatedResponse } from "@/services/analyticsService";

interface AnalyticsTableProps {
  data: PaginatedResponse<AnalyticsEmailEvent>;
  isLoading: boolean;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
}

export function AnalyticsTable({ data, isLoading, onPageChange, onRefresh }: AnalyticsTableProps) {
  const [selectedEvent, setSelectedEvent] = useState<AnalyticsEmailEvent | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const handleRowClick = (event: AnalyticsEmailEvent) => {
    setSelectedEvent(event);
    setIsPanelOpen(true);
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setSelectedEvent(null);
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "success":
        return "success";
      case "faulted":
        return "destructive";
      case "queued":
      default:
        return "default";
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMM dd, yyyy HH:mm:ss");
  };

  const renderPaginationItems = () => {
    const items = [];
    const { page, totalPages } = data;
    
    // Show first page
    if (page > 2) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => onPageChange(1)}>1</PaginationLink>
        </PaginationItem>
      );
      if (page > 3) {
        items.push(
          <PaginationItem key="start-ellipsis">
            <span className="px-3 py-2">...</span>
          </PaginationItem>
        );
      }
    }

    // Show current page and adjacent pages
    const start = Math.max(1, page - 1);
    const end = Math.min(totalPages, page + 1);
    
    for (let i = start; i <= end; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink 
            onClick={() => onPageChange(i)}
            isActive={i === page}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Show last page
    if (page < totalPages - 1) {
      if (page < totalPages - 2) {
        items.push(
          <PaginationItem key="end-ellipsis">
            <span className="px-3 py-2">...</span>
          </PaginationItem>
        );
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => onPageChange(totalPages)}>{totalPages}</PaginationLink>
        </PaginationItem>
      );
    }

    return items;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          Showing {data.data.length} of {data.total} events
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created</TableHead>
              <TableHead>Mailbox</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Processed</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="h-4 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-muted animate-pulse rounded" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-muted animate-pulse rounded" />
                  </TableCell>
                </TableRow>
              ))
            ) : data.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No email events found
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((event) => (
                <TableRow 
                  key={event.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleRowClick(event)}
                >
                  <TableCell>{formatDate(event.created_at)}</TableCell>
                  <TableCell>{event.mailbox_email || 'Unknown'}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(event.status)}>
                      {event.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {event.processed_at ? formatDate(event.processed_at) : '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {data.totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious 
                onClick={() => onPageChange(Math.max(1, data.page - 1))}
                className={data.page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            
            {renderPaginationItems()}
            
            <PaginationItem>
              <PaginationNext 
                onClick={() => onPageChange(Math.min(data.totalPages, data.page + 1))}
                className={data.page === data.totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <EmailEventDetailsPanel
        event={selectedEvent}
        isOpen={isPanelOpen}
        onClose={handlePanelClose}
      />
    </div>
  );
}